import { useEffect } from 'react'
import { isJobActive } from '@shared/types'
import type { ImageDTO, ServerEvent } from '@shared/types'
import { UNAUTHORIZED_EVENT } from '@/lib/api'
import { connectEvents } from '@/lib/sse'
import { useAuthStore } from '@/stores/useAuthStore'
import { useJobStore } from '@/stores/useJobStore'
import { useMediaStore } from '@/stores/useMediaStore'
import { useSitesStore } from '@/stores/useSitesStore'

/** Long enough to coalesce a burst of terminal events into one refetch. */
const RESYNC_DELAY_MS = 400

/**
 * The single SSE consumer. Mounted once by `AppShell`, it fans server events
 * into the stores through `getState()` so incoming traffic never re-renders the
 * shell — only the components subscribed to the touched slices.
 */
export default function EventStream(): React.ReactElement | null {
  useEffect(() => {
    let sitesTimer: number | null = null
    let mediaTimer: number | null = null

    const resyncSites = (): void => {
      if (sitesTimer !== null) return
      sitesTimer = window.setTimeout(() => {
        sitesTimer = null
        void useSitesStore.getState().load()
      }, RESYNC_DELAY_MS)
    }

    // Per-item events keep the grid live; a refetch is only for the derived
    // numbers (filter counts, totals) that the client cannot recompute.
    const resyncMedia = (siteId: string): void => {
      if (useMediaStore.getState().siteId !== siteId) return
      if (mediaTimer !== null) return
      mediaTimer = window.setTimeout(() => {
        mediaTimer = null
        void useMediaStore.getState().reload()
      }, RESYNC_DELAY_MS)
    }

    const onEvent = (event: ServerEvent): void => {
      useJobStore.getState().applyEvent(event)

      switch (event.type) {
        case 'hello':
          resyncSites()
          break

        case 'image': {
          const media = useMediaStore.getState()
          if (media.siteId !== event.siteId) break
          const patch: Partial<ImageDTO> = { status: event.status, error: event.error ?? null }
          if (event.generatedAlt !== undefined) patch.generatedAlt = event.generatedAlt
          media.applyServerPatch(event.imageId, patch)
          break
        }

        case 'saved': {
          const media = useMediaStore.getState()
          if (media.siteId !== event.siteId) break
          media.applyServerPatch(
            event.imageId,
            event.success
              ? { saved: true, error: null, savedAt: new Date().toISOString() }
              : { saved: false, error: event.error ?? 'Could not write to WordPress' }
          )
          break
        }

        case 'site':
          useSitesStore.getState().upsertFromEvent(event.siteId, {
            imageCount: event.imageCount,
            lastFetchedAt: event.lastFetchedAt
          })
          resyncSites()
          resyncMedia(event.siteId)
          break

        case 'job':
          if (!isJobActive(event.job)) {
            resyncSites()
            resyncMedia(event.job.siteId)
          }
          break

        default:
          break
      }
    }

    const disconnect = connectEvents(onEvent)

    // The auth store already drops the user on this event; re-probing the public
    // session is what keeps `needsBootstrap` / `allowRegistration` accurate for
    // the login screen the user is about to land on.
    const onUnauthorized = (): void => {
      void useAuthStore.getState().loadSession()
    }
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized)

    return () => {
      disconnect()
      window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized)
      if (sitesTimer !== null) window.clearTimeout(sitesTimer)
      if (mediaTimer !== null) window.clearTimeout(mediaTimer)
    }
  }, [])

  return null
}

export { EventStream }
